/**
 * Pure model of the Conversations tab (lot I3, step 12). No React, no API calls.
 *
 * One form for create and edit. Every limit the user sees comes from the server: the
 * configuration limit and the agency warnings from `POST .../conversations/preview`, the
 * figures from `GET .../conversations/load`. The client mirrors only the two STRUCTURAL
 * ceilings (300 cells, 5 000 runs per launch), whose 400 stays the authority.
 *
 * @see docs/plans/prompt-studio-build/I-conversations.md step 12, UI-SPEC §2.19-2.24
 */
import {
  CONVERSATION_ENGINES,
  CONVERSATION_INTENT_PROMPT_AXIS,
  CONVERSATION_MAX_CELLS,
  CONVERSATION_MAX_RUNS,
  CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS,
  CONVERSATION_MAX_TURNS,
  CONVERSATION_MAX_TURN_OPTIONS,
  CREDIT_COST_CONVERSATION_TURN,
  conversationLaunchCost,
  defaultConversationName,
  effectiveFeatureFlags,
  launchesInPeriod,
  promptPlaceKey,
  resolveConversationCadence,
  resolveConversationMaxTurns,
  resolveEntitlement,
  zoneKeyFrom,
} from '@traaker/shared';
import type {
  AllowanceFigures,
  Brand,
  BrandQuery,
  BrandQueryAxis,
  Cadence,
  ConversationEngine,
  ConversationIntentId,
  ConversationLoad,
  ConversationPersona,
  ConversationScriptSource,
  ConversationScriptedQuestion,
  GeoSuggestion,
  TargetZone,
  Tenant,
} from '@traaker/shared';
import { MONDIAL, groupFamilies } from '../promptStudioModel';
import type { LocKey } from '../promptStudioModel';
import { termsOf } from '../coverageModel';
import type { CovConversation } from '../coverageModel';

// ---------------------------------------------------------------------------
// Constants (UI-SPEC §7.6, §7.7; Contracts « Time-zone defaults »)
// ---------------------------------------------------------------------------

/** The Runs squares. */
export const CONV_RUNS = [1, 2, 3, 4, 5, 10] as const;

/** Default time zone by the brand's main country. */
export const TZ_BY_CC: Record<string, string> = {
  FR: 'Europe/Paris', BE: 'Europe/Brussels', CH: 'Europe/Zurich', LU: 'Europe/Luxembourg',
  ES: 'Europe/Madrid', IT: 'Europe/Rome', DE: 'Europe/Berlin', GB: 'Europe/London',
  PT: 'Europe/Lisbon', CA: 'America/Montreal', US: 'America/New_York',
};

/** The mock's 12 time zones (`const TZS`), « {IANA} · {pays} ». */
export const TZS: readonly { tz: string; country: string }[] = [
  { tz: 'Europe/Paris', country: 'France' },
  { tz: 'Europe/Brussels', country: 'Belgique' },
  { tz: 'Europe/Zurich', country: 'Suisse' },
  { tz: 'Europe/Luxembourg', country: 'Luxembourg' },
  { tz: 'Europe/Madrid', country: 'Espagne' },
  { tz: 'Europe/Rome', country: 'Italie' },
  { tz: 'Europe/Berlin', country: 'Allemagne' },
  { tz: 'Europe/London', country: 'Royaume Uni' },
  { tz: 'Europe/Lisbon', country: 'Portugal' },
  { tz: 'America/Montreal', country: 'Québec' },
  { tz: 'America/New_York', country: 'États Unis, côte est' },
  { tz: 'America/Los_Angeles', country: 'États Unis, côte ouest' },
];

/** The 48 half hours `00:00 … 23:30`. */
export const HOURS: readonly string[] = Array.from({ length: 48 }, (_, i) =>
  `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`,
);

/** The zone types the save accepts (`validateAndDeriveZone`): a locality / village pick would 400. */
export const CONVERSATION_ZONE_TYPES: readonly string[] = ['country', 'city', 'region', 'postcode', 'address', 'street'];

/** Debounce of the form's configuration-limit preview (UI-SPEC §2.20). */
export const PREVIEW_DEBOUNCE_MS = 400;

const DEFAULT_ENGINES: readonly ConversationEngine[] = ['perplexity', 'chatgpt'];

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

/** One scripted item of a turn (UI-SPEC §2.21). `source` absent on an old row = 'text'. */
export interface ConvItem {
  turn: number;
  text: string;
  source: ConversationScriptSource;
  refId?: string;
}

/** A place of the form: `zone: null` = Mondial (no zone, no zoneMeta, I-D4). */
export interface ConvPlace {
  key: LocKey;
  zone: TargetZone | null;
}

export const MONDIAL_PLACE: ConvPlace = { key: MONDIAL, zone: null };

export interface ConvForm {
  brandPersonaId: string | null;
  intentId: ConversationIntentId | null;
  items: ConvItem[];
  /** Agency « Plafond » 1..6; every other tier 6 (I-D1). */
  maxTurns: number;
  engines: ConversationEngine[];
  runs: number;
  languages: string[];
  places: ConvPlace[];
  scheduleHour: string;
  timezone: string;
  tags: string[];
  /**
   * A stored `startingPrompt` longer than a scripted item may be (2 000 vs 400): it stays on the
   * row (the save never sends `startingPrompt: null`) and the form shows a grey line. Never sliced.
   */
  keptStartingPrompt?: string;
}

/** The conversation engines the tier may select: `CONVERSATION_ENGINES` ∩ the tier `engines` allowlist. */
export function convEngines(tier: string): ConversationEngine[] {
  const allowed = new Set(resolveEntitlement('engines', tier).allowed ?? []);
  return CONVERSATION_ENGINES.filter((e) => allowed.has(e));
}

/** Default time zone: the brand's first location's country, else Paris. */
export function defaultTimezone(brand: Pick<Brand, 'locations'>): string {
  const cc = brand.locations?.[0]?.countryCode?.toUpperCase();
  return (cc && TZ_BY_CC[cc]) || 'Europe/Paris';
}

/** The form's place of a (list) cell zone. */
export function placeOfZone(zone: TargetZone | undefined): ConvPlace {
  return zone ? { key: promptPlaceKey(zone), zone } : MONDIAL_PLACE;
}

/** New-conversation defaults (UI-SPEC §2.20): no persona, no intent, Perplexity + ChatGPT, 1 run, Mondial. */
export function newForm(brand: Pick<Brand, 'locations' | 'strategy'>, tier: string): ConvForm {
  const allowed = convEngines(tier);
  const engines = DEFAULT_ENGINES.filter((e) => allowed.includes(e));
  return {
    brandPersonaId: null,
    intentId: null,
    items: [],
    maxTurns: CONVERSATION_MAX_TURNS,
    engines: engines.length > 0 ? engines : allowed.slice(0, 1),
    runs: 1,
    languages: [(brand.strategy?.workingLanguage || 'fr').toLowerCase()],
    places: [MONDIAL_PLACE],
    scheduleHour: '09:00',
    timezone: defaultTimezone(brand),
    tags: [],
  };
}

/** The stored items, old `{ turn, text }` rows read as free text. */
function storedItems(p: ConversationPersona): ConvItem[] {
  // The rows carry `source` / `refId` since I1; `ConversationPersona.scriptedQuestions` still
  // types the pre-I1 shape (widening it ripples through the API's own item type).
  const raw = (p.scriptedQuestions ?? []) as ConversationScriptedQuestion[];
  return raw.map((q) => ({
    turn: q.turn,
    text: q.text,
    source: q.source ?? 'text',
    ...(q.source === 'prompt' && q.refId ? { refId: q.refId } : {}),
  }));
}

/**
 * The form of a stored conversation. Languages / places / engines = the distinct values of its
 * cells, runs = their max (a non-product cell set reads as legacy, `isLegacyShape`). A stored
 * `startingPrompt` opens as a turn-1 text item (the save then sends `startingPrompt: null`),
 * unless it is longer than a scripted item may be or turn 1 is full: then it is kept.
 */
export function formFromPersona(p: ConversationPersona, brand: Pick<Brand, 'locations'>, tier: string): ConvForm {
  const engines: ConversationEngine[] = [];
  const languages: string[] = [];
  const places: ConvPlace[] = [];
  let runs = 1;
  for (const c of p.cells ?? []) {
    if (!engines.includes(c.engine)) engines.push(c.engine);
    if (!languages.includes(c.language)) languages.push(c.language);
    const place = placeOfZone(c.zoneMeta);
    if (!places.some((x) => x.key === place.key)) places.push(place);
    runs = Math.max(runs, c.runs);
  }
  let items = storedItems(p);
  let keptStartingPrompt: string | undefined;
  const start = p.startingPrompt?.trim();
  if (start) {
    const turn1 = items.filter((i) => i.turn === 1);
    if (turn1.some((i) => i.text === start)) {
      // already an option of turn 1
    } else if (start.length <= CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS && turn1.length < CONVERSATION_MAX_TURN_OPTIONS) {
      items = [{ turn: 1, text: start, source: 'text' }, ...items];
    } else {
      keptStartingPrompt = p.startingPrompt;
    }
  }
  const order = CONVERSATION_ENGINES;
  return {
    brandPersonaId: p.brandPersonaId ?? null,
    intentId: p.intentId,
    items,
    maxTurns: resolveConversationMaxTurns(tier, p.maxTurns),
    engines: [...engines].sort((a, b) => order.indexOf(a) - order.indexOf(b)),
    runs,
    languages,
    places: places.length > 0 ? places : [MONDIAL_PLACE],
    scheduleHour: p.scheduleHour ?? '09:00',
    timezone: p.timezone ?? defaultTimezone(brand),
    tags: p.tags ?? [],
    ...(keptStartingPrompt !== undefined ? { keptStartingPrompt } : {}),
  };
}

/**
 * A stored cell set that is not the full languages × places × engines product with one runs
 * value: the form shows « Configuration précédente… » (the save replaces it by the product).
 */
export function isLegacyShape(p: Pick<ConversationPersona, 'cells'>): boolean {
  const cells = p.cells ?? [];
  if (cells.length === 0) return false;
  const engines = new Set(cells.map((c) => c.engine));
  const languages = new Set(cells.map((c) => c.language));
  const places = new Set(cells.map((c) => placeOfZone(c.zoneMeta).key));
  const keys = new Set(cells.map((c) => `${c.engine}|${c.language}|${placeOfZone(c.zoneMeta).key}`));
  return (
    keys.size !== cells.length ||
    cells.length !== engines.size * languages.size * places.size ||
    new Set(cells.map((c) => c.runs)).size > 1
  );
}

/** One cell of the save body: a Mondial place has neither `zone` nor `zoneMeta` (I-D4). */
export interface CellBody {
  engine: ConversationEngine;
  language: string;
  runs: number;
  zone?: string;
  zoneMeta?: TargetZone;
}

/** The cartesian product languages × places × engines, the same runs on each cell. */
export function cellsOf(form: Pick<ConvForm, 'languages' | 'places' | 'engines' | 'runs'>): CellBody[] {
  const out: CellBody[] = [];
  for (const language of form.languages) {
    for (const place of form.places) {
      for (const engine of form.engines) {
        out.push({
          engine,
          language,
          runs: form.runs,
          ...(place.zone ? { zone: zoneKeyFrom(place.zone as GeoSuggestion), zoneMeta: place.zone } : {}),
        });
      }
    }
  }
  return out;
}

/** Runs per launch of the form (C18 `conversationLaunchCost`, no second helper). */
export function runsPerLaunch(form: Pick<ConvForm, 'languages' | 'places' | 'engines' | 'runs'>): number {
  return conversationLaunchCost(cellsOf(form));
}

/** Distinct scripted turns, ascending. */
export function scriptedTurns(items: readonly ConvItem[]): number[] {
  return [...new Set(items.map((i) => i.turn))].sort((a, b) => a - b);
}

/**
 * The save body (create, or PATCH when `original` is set), and the preview body. Always the
 * whole configuration; never `cadence` (the brand cadence lives in the footer), never
 * `comparisonSet` / `comparatorTargetIds` / `listSize`. An edit clears the stop criterion and the
 * starting prompt (now a turn-1 item; a kept long one is left untouched). No inline trait is sent:
 * the save always links a persona, which REMOVES every inline trait server-side, and the API
 * refuses `tone: null` (« tone invalide : null », dev 2026-11-01).
 */
export function personaPayload(
  form: ConvForm,
  original: ConversationPersona | null,
  isAgency: boolean,
  personaName: string,
): Record<string, unknown> {
  const maxTurns = isAgency ? form.maxTurns : CONVERSATION_MAX_TURNS;
  const items = form.items.filter((i) => i.turn <= maxTurns);
  const body: Record<string, unknown> = {
    name: form.intentId ? defaultConversationName(personaName, form.intentId) : personaName,
    brandPersonaId: form.brandPersonaId,
    intentId: form.intentId,
    brandAwareness: 'unaware',
    mode: items.length > 0 ? 'mixed' : 'improvised',
    scriptedQuestions: items.map((i) => ({
      turn: i.turn,
      text: i.text,
      source: i.source,
      ...(i.source === 'prompt' && i.refId ? { refId: i.refId } : {}),
    })),
    maxTurns,
    cells: cellsOf(form),
    scheduleHour: form.scheduleHour,
    timezone: form.timezone,
    tags: form.tags,
  };
  if (original) {
    body.stopCriterion = null;
    if (form.keptStartingPrompt === undefined) body.startingPrompt = null;
  }
  return body;
}

/** What the form needs before it can be saved or previewed. */
export function formMissing(form: Pick<ConvForm, 'brandPersonaId' | 'intentId'>): 'persona' | 'intent' | null {
  if (!form.brandPersonaId) return 'persona';
  if (!form.intentId) return 'intent';
  return null;
}

// ---------------------------------------------------------------------------
// Turn picker (UI-SPEC §2.21)
// ---------------------------------------------------------------------------

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Accent-insensitive « contains ». */
export function matches(text: string, q: string): boolean {
  return fold(text).includes(fold(q.trim()));
}

/**
 * « Mes prompts » of a turn: the active family roots with an axis (never pending), text ≤ 400.
 * Turn 1 with an intent, while its filter is on: the intent's axis only
 * (`CONVERSATION_INTENT_PROMPT_AXIS`: Avis → Image, Comparaison → its map value). Search: text + tags.
 */
export function promptsForTurn(
  queries: readonly BrandQuery[],
  intentId: ConversationIntentId | null,
  turn: number,
  q: string,
  filterOnIntent = true,
): BrandQuery[] {
  const axis: BrandQueryAxis | null =
    turn === 1 && intentId && filterOnIntent ? CONVERSATION_INTENT_PROMPT_AXIS[intentId] : null;
  // One entry per family (its root): the save fills the item's other languages from the family.
  return groupFamilies([...queries]).map((f) => f.root).filter(
    (r) =>
      r.status === 'active' &&
      r.axis !== 'pending' &&
      r.text.length <= CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS &&
      (!axis || r.axis === axis) &&
      (!q.trim() || matches(r.text, q) || (r.tags ?? []).some((t) => matches(t, q))),
  );
}

/** The Objections source: the linked persona's, then Brand Knowledge's (deduped, in order). */
export function objectionsFor(
  personaObjections: readonly string[] | undefined,
  brandObjections: readonly string[] | undefined,
): Array<{ text: string; from: 'persona' | 'knowledge' }> {
  const out: Array<{ text: string; from: 'persona' | 'knowledge' }> = [];
  for (const text of personaObjections ?? []) if (text.trim() && !out.some((o) => o.text === text)) out.push({ text, from: 'persona' });
  for (const text of brandObjections ?? []) if (text.trim() && !out.some((o) => o.text === text)) out.push({ text, from: 'knowledge' });
  return out;
}

/** Toggle one item of a turn: removes it when listed, else adds it (≤ 5 per turn, no duplicate text). */
export function toggleItem(items: readonly ConvItem[], item: ConvItem): ConvItem[] {
  const same = (i: ConvItem) => i.turn === item.turn && i.text === item.text;
  if (items.some(same)) return items.filter((i) => !same(i));
  if (items.filter((i) => i.turn === item.turn).length >= CONVERSATION_MAX_TURN_OPTIONS) return [...items];
  return [...items, item];
}

/** The agency ceiling lowered: items past it are removed (the server 400s them). */
export function withMaxTurns(form: ConvForm, maxTurns: number): ConvForm {
  return { ...form, maxTurns, items: form.items.filter((i) => i.turn <= maxTurns) };
}

// ---------------------------------------------------------------------------
// Places
// ---------------------------------------------------------------------------

/**
 * Toggle a place (UI-SPEC §2.10): Mondial is exclusive; the last place cannot be dropped.
 */
export function togglePlace(places: readonly ConvPlace[], place: ConvPlace): ConvPlace[] {
  if (place.key === MONDIAL) return [MONDIAL_PLACE];
  const rest = places.filter((p) => p.key !== MONDIAL);
  if (rest.some((p) => p.key === place.key)) {
    const next = rest.filter((p) => p.key !== place.key);
    return next.length > 0 ? next : [...places];
  }
  return [...rest, place];
}

/** Toggle a language; the last one cannot be dropped. */
export function toggleLanguage(languages: readonly string[], code: string): string[] {
  if (languages.includes(code)) return languages.length > 1 ? languages.filter((l) => l !== code) : [...languages];
  return [...languages, code];
}

/** Toggle an engine; the last one cannot be dropped. */
export function toggleEngine(engines: readonly ConversationEngine[], e: ConversationEngine): ConversationEngine[] {
  if (engines.includes(e)) return engines.length > 1 ? engines.filter((x) => x !== e) : [...engines];
  const order = CONVERSATION_ENGINES;
  return [...engines, e].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

/**
 * The place picker's options (C14, I3 H1). The save needs finite coordinates and the persona LIST
 * serves none: the conversation's OWN stored places are resent as listed (the save restores their
 * coordinates); any other known zone is offered only with finite coordinates, and only of a type
 * the save accepts. The rest stays reachable through the search.
 */
export function placeOptions(
  own: readonly ConvPlace[],
  known: Map<LocKey, TargetZone>,
): ConvPlace[] {
  const out: ConvPlace[] = [];
  const same = (a: TargetZone, b: TargetZone) =>
    zoneKeyFrom(a as GeoSuggestion) === zoneKeyFrom(b as GeoSuggestion) && a.type === b.type;
  for (const p of own) if (p.zone && !out.some((o) => o.key === p.key)) out.push(p);
  for (const [key, z] of known) {
    if (!isSaveableZone(z)) continue;
    if (out.some((o) => o.key === key || (o.zone && same(o.zone, z)))) continue;
    out.push({ key, zone: z });
  }
  return out;
}

/** A zone the save accepts as a NEW place: an accepted type with finite coordinates. */
export function isSaveableZone(z: TargetZone): boolean {
  return (
    CONVERSATION_ZONE_TYPES.includes(z.type) &&
    typeof z.latitude === 'number' && Number.isFinite(z.latitude) &&
    typeof z.longitude === 'number' && Number.isFinite(z.longitude)
  );
}

/** Every distinct zone of the brand's conversations (C14 known zones, Couverture lanes). */
export function conversationZones(personas: readonly ConversationPersona[]): TargetZone[] {
  const out = new Map<string, TargetZone>();
  for (const p of personas) {
    for (const c of p.cells ?? []) {
      if (c.zoneMeta && !out.has(promptPlaceKey(c.zoneMeta))) out.set(promptPlaceKey(c.zoneMeta), c.zoneMeta);
    }
  }
  return [...out.values()];
}

// ---------------------------------------------------------------------------
// Couverture (UI-SPEC §6.7)
// ---------------------------------------------------------------------------

/**
 * Couverture's key of a conversation cell's place: the listed zone carries no postcode / address
 * (`listCell`), so a prompt zone of the same place (type, country, label) gives its full key; else
 * the zone's own `promptPlaceKey`. Mondial = `'∅'`. The same key space as B's lanes (C1 `placeKey`).
 */
export function convPlaceKey(zone: TargetZone | undefined, known: Map<LocKey, TargetZone>): LocKey {
  if (!zone) return MONDIAL;
  for (const [key, z] of known) {
    if (z.type === zone.type && z.countryCode === zone.countryCode && z.label === zone.label) return key;
  }
  return promptPlaceKey(zone);
}

/** The prompt axis of a conversation's intent (rail dot, turn-1 filter, Couverture axis). */
export function intentAxis(intentId: ConversationIntentId): BrandQueryAxis {
  return CONVERSATION_INTENT_PROMPT_AXIS[intentId];
}

/**
 * One Couverture entry per ACTIVE conversation: axis = its intent's axis; places = each of its
 * places; terms = the vocabulary terms of its scripted prompts on that same axis (deduped), else
 * `[]` = (aucun). An item without `refId` (old row) brings no term.
 */
export function covConversations(
  personas: readonly ConversationPersona[],
  queriesById: Map<string, BrandQuery>,
  vocabByAxis: Record<BrandQueryAxis, string[]>,
  known: Map<LocKey, TargetZone> = new Map(),
): CovConversation[] {
  return personas
    .filter((p) => p.status === 'active')
    .map((p) => {
      const ax = intentAxis(p.intentId);
      const locs = [...new Set((p.cells ?? []).map((c) => convPlaceKey(c.zoneMeta, known)))];
      const terms = new Set<string>();
      for (const item of storedItems(p)) {
        if (item.source !== 'prompt' || !item.refId) continue;
        const q = queriesById.get(item.refId);
        if (!q || q.axis !== ax) continue;
        for (const term of termsOf(q, vocabByAxis[ax]) ?? []) terms.add(term);
      }
      return { ax, locs, terms: [...terms] };
    });
}

// ---------------------------------------------------------------------------
// Figures (display only: every limit comes from the server)
// ---------------------------------------------------------------------------

/**
 * The cadence THIS conversation resolves to (the server's `resolveConversationCadence`): the
 * brand cadence when one is set (`load.cadence` is then its resolution), else the row's own
 * legacy cadence, else the tier default.
 */
export function conversationCadence(
  load: Pick<ConversationLoad, 'cadence' | 'cadenceIsSet'> | null,
  tier: string,
  original: Pick<ConversationPersona, 'cadence'> | null,
): Cadence {
  if (load?.cadenceIsSet && load.cadence) return load.cadence;
  return resolveConversationCadence(tier, original ?? {});
}

/** Agency credits per month of one conversation: runs per launch × ceiling × 2 × launches per period. */
export function conversationCreditsPerMonth(perLaunch: number, maxTurns: number, cadence: Cadence): number {
  return perLaunch * resolveConversationMaxTurns('agency', maxTurns) * CREDIT_COST_CONVERSATION_TURN * launchesInPeriod(cadence);
}

export interface ConfirmFigures {
  languages: number;
  places: number;
  engines: number;
  runs: number;
  perLaunch: number;
  launchesPerMonth: number;
  /** cells > CONVERSATION_MAX_CELLS (the client mirror of the 5b 400). */
  overCells: boolean;
  /** runs per launch > CONVERSATION_MAX_RUNS. */
  overRuns: boolean;
}

/** The SaveConfirmation equation and the two structural ceilings (never the quota). */
export function confirmFigures(form: Pick<ConvForm, 'languages' | 'places' | 'engines' | 'runs'>, cadence: Cadence): ConfirmFigures {
  const cells = form.languages.length * form.places.length * form.engines.length;
  const perLaunch = cells * form.runs;
  return {
    languages: form.languages.length,
    places: form.places.length,
    engines: form.engines.length,
    runs: form.runs,
    perLaunch,
    launchesPerMonth: launchesInPeriod(cadence),
    overCells: cells > CONVERSATION_MAX_CELLS,
    overRuns: perLaunch > CONVERSATION_MAX_RUNS,
  };
}

/** The structural reason that disables « Créer » / « Enregistrer » (cells before runs), or null. */
export function structuralReason(f: Pick<ConfirmFigures, 'overCells' | 'overRuns'>): 'maxCells' | 'maxRuns' | null {
  if (f.overCells) return 'maxCells';
  if (f.overRuns) return 'maxRuns';
  return null;
}

/**
 * I3 H2: a NON-agency allowance of -1 is not « unlimited »: every launch is refused until an admin
 * sets a finite override. The form, the save dialog and the footer say so.
 */
export function isUnbounded(figures: Pick<AllowanceFigures, 'allowance'> | null | undefined, isAgency: boolean): boolean {
  return !isAgency && figures?.allowance === -1;
}

/** « Il reste {left} runs par mois » of the save dialog: `allowance − sustainableNeed` of the last preview. */
export function runsLeft(figuresAfter: Pick<AllowanceFigures, 'allowance' | 'sustainableNeed'>): number {
  return figuresAfter.allowance - figuresAfter.sustainableNeed;
}

/**
 * Tab visibility (I-D1, I3 medium): the feature flag (the routes 403 without it) AND the brand's
 * `conversationRuns` allowance from `GET .../allowances` is not 0 (-1 shows it). Unknown → hidden.
 */
export function conversationsVisible(
  tenant: Pick<Tenant, 'tier' | 'settings'> | null | undefined,
  conversationRuns: Pick<AllowanceFigures, 'allowance'> | null | undefined,
): boolean {
  if (!tenant || !conversationRuns) return false;
  return !!effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags).conversationsEnabled && conversationRuns.allowance !== 0;
}

// ---------------------------------------------------------------------------
// Cadence squares (UI-SPEC §2.24, I-D3)
// ---------------------------------------------------------------------------

/** [B] 1..7 days; [A] 1 2 3 5 7 14, « 30 » = one calendar month, « Manuel ». */
export function cadencePips(isAgency: boolean): Cadence[] {
  if (!isAgency) return [1, 2, 3, 4, 5, 6, 7].map((d) => ({ interval: d, unit: 'days' as const }));
  return [
    ...[1, 2, 3, 5, 7, 14].map((d) => ({ interval: d, unit: 'days' as const })),
    { interval: 1, unit: 'months' as const },
    'off' as const,
  ];
}

/** A square's identity. A non-agency week cadence (pro's tier default `{ 1, 'weeks' }`) is the « 7 » square. */
export function cadenceKey(c: Cadence): string {
  if (c === 'off') return 'off';
  if (c.unit === 'weeks') return `days:${c.interval * 7}`;
  return `${c.unit}:${c.interval}`;
}

/** The selected square: `load.cadence` when it is one of the squares (a stored `{ 30, 'days' }` is none). */
export function selectedPip(cadence: Cadence | null, pips: readonly Cadence[]): string | null {
  if (cadence === null) return null;
  const k = cadenceKey(cadence);
  return pips.some((p) => cadenceKey(p) === k) ? k : null;
}

// ---------------------------------------------------------------------------
// Server errors (ApiError.body), client strings
// ---------------------------------------------------------------------------

export type SaveError =
  | { kind: 'refused'; figuresAfter: AllowanceFigures }
  | { kind: 'structural'; reason: 'maxCells' | 'maxRuns' }
  | { kind: 'personaLimit' }
  | { kind: 'conflict' }
  | { kind: 'unavailable' }
  | { kind: 'unbounded' }
  | { kind: 'network' }
  | { kind: 'invalid'; message: string }
  | { kind: 'failed' };

/**
 * One server failure of a save / preview / reactivation, from `ApiError.status` + `.body`:
 * 409 ALLOWANCE_EXCEEDED (its `figuresAfter`), 409 CONFIG_CONFLICT, 429 QUOTA_EXCEEDED
 * `conversation_personas`, 503 QUOTA_UNAVAILABLE / CONFIG_UNAVAILABLE / CONFIG_WRITE_FAILED, a
 * structural 400 (5b), another 400 (its message), and a failure with no HTTP status (network).
 */
export function saveErrorOf(err: unknown): SaveError {
  const e = err as { status?: number; body?: { code?: string; detail?: string; metric?: string; message?: string; figuresAfter?: AllowanceFigures } };
  const status = typeof e?.status === 'number' ? e.status : undefined;
  if (status === undefined) return { kind: 'network' };
  const body = e.body ?? {};
  if (status === 409 && body.code === 'ALLOWANCE_EXCEEDED' && body.figuresAfter) return { kind: 'refused', figuresAfter: body.figuresAfter };
  if (status === 409 && body.code === 'CONFIG_CONFLICT') return { kind: 'conflict' };
  if (status === 429 && body.code === 'QUOTA_EXCEEDED' && body.metric === 'conversation_personas') return { kind: 'personaLimit' };
  if (status === 503 && body.code === 'QUOTA_UNAVAILABLE' && body.detail === 'allowance_unbounded') return { kind: 'unbounded' };
  if (status === 503) return { kind: 'unavailable' };
  if (status === 400) {
    const m = body.message ?? '';
    if (m.startsWith('Au plus 300 combinaisons')) return { kind: 'structural', reason: 'maxCells' };
    if (m.startsWith('Au plus 5 000 runs')) return { kind: 'structural', reason: 'maxRuns' };
    if (m) return { kind: 'invalid', message: m };
  }
  return { kind: 'failed' };
}

/** The i18n key of a save error's line (`refused` uses `limitReason` instead, `invalid` its message). */
export function saveErrorKey(e: SaveError): string | null {
  switch (e.kind) {
    case 'structural': return e.reason === 'maxCells' ? 'promptStudio.conv.maxCells' : 'promptStudio.save.maxRuns';
    case 'personaLimit': return 'promptStudio.conv.personaLimit';
    case 'conflict': return 'promptStudio.conv.conflict';
    case 'unavailable': return 'promptStudio.conv.unavailable';
    case 'unbounded': return 'promptStudio.conv.unbounded';
    case 'network': return 'promptStudio.conv.network';
    case 'failed': return 'promptStudio.common.saveFailed';
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Request discipline (U15: one preview per pause, one POST per « Confirmer »)
// ---------------------------------------------------------------------------

export type PreviewState<T> =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'done'; result: T }
  | { status: 'failed' };

/**
 * The form's debounced preview: `schedule(body)` restarts the 400 ms timer; only the LAST body is
 * sent and only its answer is kept (a slower earlier answer is dropped). `pending` from the first
 * schedule until the answer, so the save button stays disabled while a preview is due or in flight.
 */
export function createPreviewer<B, T>(
  send: (body: B) => Promise<T>,
  onState: (s: PreviewState<T>) => void,
  delayMs = PREVIEW_DEBOUNCE_MS,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let seq = 0;
  return {
    schedule(body: B) {
      const mine = ++seq;
      if (timer) clearTimeout(timer);
      onState({ status: 'pending' });
      timer = setTimeout(() => {
        timer = null;
        send(body).then(
          (result) => { if (mine === seq) onState({ status: 'done', result }); },
          () => { if (mine === seq) onState({ status: 'failed' }); },
        );
      }, delayMs);
    },
    cancel() {
      seq++;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}

/**
 * I3 H3: « Confirmer » is inert while its own request is in flight (`createPersona` has no
 * idempotency: a second POST is a second scheduled conversation). A call while one runs returns
 * the running promise; nothing is retried.
 */
export function singleFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let running: Promise<T> | null = null;
  return () => {
    if (running) return running;
    running = fn().finally(() => { running = null; });
    return running;
  };
}

// ---------------------------------------------------------------------------
// List: search, groups, filters (UI-SPEC §2.19, §2.15)
// ---------------------------------------------------------------------------

/** « Désactivés »: user pauses and archives without a reason; « Mis en pause (offre) »: plan_limit. */
export function convGroups(personas: readonly ConversationPersona[]): {
  active: ConversationPersona[];
  disabled: ConversationPersona[];
  paused: ConversationPersona[];
} {
  return {
    active: personas.filter((p) => p.status === 'active'),
    disabled: personas.filter((p) => p.status === 'paused' || (p.status === 'archived' && p.archivedReason !== 'plan_limit')),
    paused: personas.filter((p) => p.status === 'archived' && p.archivedReason === 'plan_limit'),
  };
}

/** The row's title: the LIVE Brand Knowledge persona name, else the stored name (unlinked legacy row). */
export function personaTitle(p: ConversationPersona, brandPersonaNames: Map<string, string>): string {
  return (p.brandPersonaId && brandPersonaNames.get(p.brandPersonaId)) || p.name;
}

export type ConvFacet = 'persona' | 'intent' | 'vocab' | 'zone' | 'language' | 'tags';
export const CONV_FACETS: readonly ConvFacet[] = ['persona', 'intent', 'vocab', 'zone', 'language', 'tags'];

/** The Conversations tab's filter (its own state: the Prompts filter is family-based). */
export interface ConvFilter {
  facets: Partial<Record<ConvFacet, string[]>>;
}
export const EMPTY_CONV_FILTER: ConvFilter = { facets: {} };

/** Everything a conversation offers to each facet. vocab keys = `${axis}|${term}`, `${axis}|` = (aucun). */
export interface ConvFacetValues {
  persona: string[];
  intent: string[];
  vocab: string[];
  zone: string[];
  language: string[];
  tags: string[];
}

export function convFacetValues(
  p: ConversationPersona,
  cov: CovConversation | undefined,
  known: Map<LocKey, TargetZone>,
): ConvFacetValues {
  const ax = intentAxis(p.intentId);
  const terms = cov?.terms ?? [];
  return {
    persona: [p.brandPersonaId ?? ''],
    intent: [p.intentId],
    vocab: terms.length > 0 ? terms.map((t) => `${ax}|${t}`) : [`${ax}|`],
    zone: [...new Set((p.cells ?? []).map((c) => convPlaceKey(c.zoneMeta, known)))],
    language: [...new Set((p.cells ?? []).map((c) => c.language))],
    tags: p.tags ?? [],
  };
}

/**
 * Search (persona name, intent label, tags; accent-insensitive) then every facet; a facet keeps a
 * conversation when one of its values is selected. `values(p)` = `convFacetValues`.
 */
export function applyConvFilter(
  personas: readonly ConversationPersona[],
  q: string,
  f: ConvFilter,
  values: (p: ConversationPersona) => ConvFacetValues,
  searchText: (p: ConversationPersona) => string,
): ConversationPersona[] {
  return personas.filter((p) => {
    if (q.trim() && !matches(searchText(p), q)) return false;
    const v = values(p);
    for (const facet of CONV_FACETS) {
      const sel = f.facets[facet];
      if (sel?.length && !sel.some((s) => v[facet].includes(s))) return false;
    }
    return true;
  });
}

/** Counts of one facet's values (in conversations), applying every OTHER facet. */
export function convFacetCounts(
  personas: readonly ConversationPersona[],
  q: string,
  f: ConvFilter,
  facet: ConvFacet,
  values: (p: ConversationPersona) => ConvFacetValues,
  searchText: (p: ConversationPersona) => string,
): Record<string, number> {
  const others: ConvFilter = { facets: { ...f.facets, [facet]: undefined } };
  const counts: Record<string, number> = {};
  for (const p of applyConvFilter(personas, q, others, values, searchText)) {
    for (const v of new Set(values(p)[facet])) counts[v] = (counts[v] ?? 0) + 1;
  }
  return counts;
}

/** The Couverture 🤖 click (UI-SPEC §6.6): the cell's term and place. */
export function convFilterFromCell(c: { ax: BrandQueryAxis; tp: string | null; loc: LocKey }): ConvFilter {
  return { facets: { vocab: [`${c.ax}|${c.tp ?? ''}`], zone: [c.loc] } };
}

export function convFilterCount(f: ConvFilter): number {
  return Object.values(f.facets).reduce((n, v) => n + (v?.length ?? 0), 0);
}
